import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { getFinanceUploadUrl, processFinanceUpload, getUploadJobStatus } from '../../services/api';

const ACCEPTED_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/x-iwork-keynote-sffnumbers': '.numbers',
};

const SHEET_OPTIONS = [
  { value: 'financial', label: 'Financial Upload' },
  { value: 'aop', label: 'AOP Targets' },
];

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Column indices for month columns in the Financial Upload sheet (Row 2 = ACTUAL markers)
// Columns: 0-9 are metadata, 10=jan, 11=feb, ... 21=dec
const MONTH_COL_START = 10;

const STEPS = [
  { key: 'select', label: 'Select File' },
  { key: 'uploading', label: 'Uploading' },
  { key: 'processing', label: 'Processing' },
  { key: 'complete', label: 'Complete' },
];

function FinanceImport({ onNavigate }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [sheetType, setSheetType] = useState('financial');
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [detectedCloseMonth, setDetectedCloseMonth] = useState(null);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = useCallback((f) => {
    if (!f) return 'No file selected';
    const ext = f.name.toLowerCase().split('.').pop();
    const validExts = ['xlsx', 'xls', 'numbers'];
    if (!ACCEPTED_TYPES[f.type] && !validExts.includes(ext)) {
      return 'Invalid file type. Only spreadsheet files (.xlsx, .xls, .numbers) are accepted.';
    }
    if (f.size > 10 * 1024 * 1024) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  }, []);

  const detectCloseMonth = useCallback((f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Look for the Financial Upload sheet
        const sheetName = workbook.SheetNames.find(
          (n) => n.toLowerCase().includes('financial')
        );
        if (!sheetName) {
          setDetectedCloseMonth(null);
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        // Row 2 (index 2) contains "ACTUAL" markers above month columns
        const markerRow = rows[2] || [];
        let lastActual = 0;
        for (let col = MONTH_COL_START; col < MONTH_COL_START + 12; col++) {
          const val = String(markerRow[col] || '').trim().toUpperCase();
          if (val === 'ACTUAL') {
            lastActual = col - MONTH_COL_START + 1; // 1=Jan, 2=Feb, etc.
          }
        }

        setDetectedCloseMonth(lastActual > 0 ? String(lastActual).padStart(2, '0') : null);
      } catch {
        setDetectedCloseMonth(null);
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleFile = useCallback((f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setFile(f);
    detectCloseMonth(f);
  }, [validateFile, detectCloseMonth]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  // Poll for job completion when in processing state
  useEffect(() => {
    if (step !== 'processing' || !jobId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getUploadJobStatus('financials', jobId);
        if (cancelled) return;

        if (status.status === 'COMPLETE') {
          setResult(status);
          setStep('complete');
        } else if (status.status === 'ERROR') {
          setError(status.error || 'Processing failed');
          setStep('select');
        }
        // PROCESSING → keep polling
      } catch {
        // Ignore transient fetch errors, keep polling
      }
    };

    const interval = setInterval(poll, 2000);
    poll(); // immediate first check
    return () => { cancelled = true; clearInterval(interval); };
  }, [step, jobId]);

  async function handleUpload() {
    if (!file) return;

    // Validate close month detection for financial uploads
    if (sheetType === 'financial' && !detectedCloseMonth) {
      setError('Could not detect close month from spreadsheet. Ensure Row 3 has "ACTUAL" above closed month columns.');
      return;
    }

    setError('');

    try {
      // Step 1: Get presigned URL
      setStep('uploading');
      let uploadUrl, s3Key;
      try {
        ({ uploadUrl, s3Key } = await getFinanceUploadUrl(file.name, file.type));
      } catch (e) {
        throw new Error(`Step 1 (get upload URL): ${e.message}`);
      }

      // Step 2: Upload to S3
      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`S3 returned ${response.status}: ${text.slice(0, 200)}`);
        }
      } catch (e) {
        if (e.message.startsWith('S3 returned')) throw e;
        throw new Error(`Step 2 (S3 upload): ${e.message}`);
      }

      // Step 3: Trigger backend processing (returns 202 immediately)
      setStep('processing');
      try {
        const { jobId: id } = await processFinanceUpload({
          s3Key,
          fiscalYear,
          sheet: sheetType,
          closeMonth: sheetType === 'financial' ? detectedCloseMonth : undefined,
        });

        setJobId(id);
        // Polling useEffect will pick up from here
      } catch (e) {
        throw new Error(`Step 3 (process upload): ${e.message}`);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      setStep('select');
    }
  }

  function handleReset() {
    setStep('select');
    setFile(null);
    setError('');
    setResult(null);
    setJobId(null);
    setDetectedCloseMonth(null);
  }

  const currentStepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="form-page">
      <div className="form-card">
        <h2 style={{
          fontFamily: 'Kode Mono, monospace',
          fontSize: '18px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '24px',
          color: 'var(--bcore-text)',
        }}>
          Finance Import
        </h2>

        {/* Progress Steps */}
        <div className="upload-steps">
          {STEPS.map((s, idx) => (
            <div
              key={s.key}
              className={`upload-step${idx <= currentStepIdx ? ' active' : ''}${idx < currentStepIdx ? ' completed' : ''}`}
            >
              <div className="upload-step-dot">
                {idx < currentStepIdx ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span className="upload-step-label">{s.label}</span>
              {idx < STEPS.length - 1 && <div className="upload-step-line" />}
            </div>
          ))}
        </div>

        {/* Select File State */}
        {step === 'select' && (
          <>
            {/* Upload Options */}
            <div className="form-section" style={{ marginBottom: '20px' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Sheet Type <span className="required">*</span></label>
                  <select
                    className="form-input"
                    value={sheetType}
                    onChange={(e) => setSheetType(e.target.value)}
                  >
                    {SHEET_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Fiscal Year <span className="required">*</span></label>
                  <select
                    className="form-input"
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(e.target.value)}
                  >
                    <option value="2025">FY2025</option>
                    <option value="2026">FY2026</option>
                    <option value="2027">FY2027</option>
                  </select>
                </div>

                {sheetType === 'financial' && detectedCloseMonth && (
                  <div className="form-group">
                    <label className="form-label">Close Month</label>
                    <div className="form-input" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'default',
                      opacity: 0.9,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {MONTH_NAMES[parseInt(detectedCloseMonth, 10)]} (detected from spreadsheet)
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dropzone */}
            <div
              className={`upload-dropzone${dragActive ? ' drag-active' : ''}${file ? ' has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.numbers,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="upload-file-info">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                  <div className="upload-file-name">{file.name}</div>
                  <div className="upload-file-size">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <button
                    className="upload-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="upload-dropzone-content">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div className="upload-dropzone-text">
                    Drag and drop your financial spreadsheet here
                  </div>
                  <div className="upload-dropzone-hint">
                    or click to browse &bull; .xlsx, .xls, .numbers &bull; Max 10MB
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="form-api-error">{error}</div>
            )}

            <div className="form-actions">
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!file}
              >
                Upload & Process
              </button>
            </div>
          </>
        )}

        {/* Uploading State */}
        {step === 'uploading' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Uploading Spreadsheet</div>
            <div className="upload-processing-sub">
              Transferring {file?.name} to secure storage...
            </div>
          </div>
        )}

        {/* Processing State */}
        {step === 'processing' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Processing Financial Data</div>
            <div className="upload-processing-sub">
              Parsing spreadsheet and updating financial records...
            </div>
          </div>
        )}

        {/* Complete State */}
        {step === 'complete' && (
          <div className="upload-processing">
            <div className="upload-complete-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="upload-processing-title">Import Complete</div>
            <div className="upload-processing-sub">
              Financial data has been processed successfully.
            </div>
            {result && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: 'var(--bcore-hover-subtle)',
                borderRadius: '8px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '12px',
                color: 'var(--bcore-text-muted)',
                textAlign: 'left',
                maxWidth: '360px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: 'var(--bcore-text-dim)' }}>Records Written:</span>{' '}
                  <span style={{ color: 'var(--bcore-accent)' }}>{result.records_written}</span>
                </div>
                {result.programs_processed !== undefined && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Programs Processed:</span>{' '}
                    <span style={{ color: 'var(--bcore-accent)' }}>{result.programs_processed}</span>
                  </div>
                )}
                {result.batch_id && (
                  <div>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Batch ID:</span>{' '}
                    <span style={{ opacity: 0.6 }}>{result.batch_id}</span>
                  </div>
                )}
              </div>
            )}
            <div className="form-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
              <button className="btn btn-outline" onClick={handleReset}>
                Upload Another
              </button>
              <button className="btn btn-primary" onClick={() => onNavigate('forecast-dashboard')}>
                View Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

FinanceImport.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

export default FinanceImport;
